
import React from 'react';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { ThemeToggle } from './ThemeToggle';
import { useIsMobile } from '@/hooks/use-mobile';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/hooks/useNotifications';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export const NavBar: React.FC = () => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { 
    notifications, 
    unreadCount, 
    markAsRead,
    showAllNotifications 
  } = useNotifications();

  // Определяем заголовок в зависимости от роли пользователя
  const headerTitle = user?.role === 'admin' ? 'Центр контроля' : 'Кабинет райдера';

  const handleNotificationClick = (id: string) => {
    markAsRead(id);
  };

  // При открытии меню уведомлений показываем их все
  const handleNotificationsOpen = (open: boolean) => {
    if (open && unreadCount > 0) {
      showAllNotifications();
    }
  };

  // Форматирование даты уведомления в формате "Сегодня, 10:30" или "21 апреля, 12:10"
  const formatNotificationDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      
      // Проверяем, сегодня ли это
      if (date.toDateString() === now.toDateString()) {
        return `Сегодня, ${format(date, 'HH:mm')}`;
      }
      
      // Проверяем, вчера ли это
      if (date.toDateString() === yesterday.toDateString()) {
        return `Вчера, ${format(date, 'HH:mm')}`;
      }
      
      // Иначе возвращаем полную дату
      return format(date, 'd MMMM, HH:mm', { locale: ru });
    } catch (e) {
      console.error('Ошибка форматирования даты:', e);
      return dateString;
    }
  };

  return (
    <header className="border-b bg-background h-16 flex items-center px-4 sticky top-0 z-10">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          {isMobile && (
            <SidebarTrigger className="mr-2" />
          )}
        </div>
        
        <div className="flex-1 flex justify-center">
          <span className="font-bold text-xl">{headerTitle}</span>
        </div>
        
        <div className="flex items-center space-x-2">
          <DropdownMenu onOpenChange={handleNotificationsOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 h-4 w-4 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[340px] max-h-[400px] overflow-auto">
              <DropdownMenuLabel className="flex justify-between items-center">
                <span>Уведомления</span>
                <span className="text-xs text-muted-foreground">
                  {notifications.length > 0 ? `${unreadCount} непрочитанных` : ""}
                </span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {notifications.length > 0 ? (
                notifications.map((notification) => (
                  <DropdownMenuItem
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification.id)}
                    className={cn(
                      "flex flex-col items-start p-3 cursor-pointer hover:bg-muted/80",
                      !notification.read && "bg-muted/60"
                    )}
                  >
                    <div className="flex w-full items-center justify-between">
                      <span className={cn(
                        "font-medium",
                        notification.type === 'warning' && "text-orange-500",
                        notification.type === 'system' && "text-blue-500",
                        notification.type === 'payment' && "text-green-500"
                      )}>
                        {notification.title}
                      </span>
                      {!notification.read && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full ml-2"></span>
                      )}
                    </div>
                    <span className="text-sm text-muted-foreground mt-1">{notification.message}</span>
                    <span className="text-xs text-muted-foreground mt-2">
                      {formatNotificationDate(notification.timestamp)}
                    </span>
                  </DropdownMenuItem>
                ))
              ) : (
                <div className="p-3 text-center text-sm text-muted-foreground">
                  У вас нет уведомлений
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
}
